<div class="h-full flex flex-col overflow-hidden">
  <!-- Header -->
  <div class="border-b border-zinc-200 dark:border-zinc-700 px-6 py-4 flex-none bg-zinc-50/50 dark:bg-zinc-800/50">
    <div class="flex items-center justify-between gap-4">
      <div class="flex items-center gap-3 min-w-0">
        <button
          data-testid="template-detail-back-btn"
          routerLink="/templates"
          title="Back to Templates"
          aria-label="Back to Templates"
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-600 hover:text-zinc-700 dark:hover:text-zinc-200 cursor-pointer transition-colors"
        >
          <mat-icon class="!text-base">arrow_back</mat-icon>
        </button>
        <div class="min-w-0">
          <h2 class="text-lg font-semibold text-zinc-900 dark:text-zinc-100 truncate" [title]="template()?.name || 'Template Details'">
            {{ template()?.name || 'Template Details' }}
          </h2>
          <!-- Meta strip: the at-a-glance facts, so the body can lead with content -->
          @if (template(); as tpl) {
            <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
              <span class="inline-flex items-center gap-1">
                <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-none" aria-hidden="true">person</mat-icon>
                {{ tpl.author }}
              </span>
              <span class="inline-flex items-center gap-1 cursor-help" [matTooltip]="'Updated ' + formatDate(tpl.updatedAt) + ' · Created ' + formatDate(tpl.createdAt)">
                <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-none" aria-hidden="true">schedule</mat-icon>
                Updated {{ relativeTime(tpl.updatedAt) }}
              </span>
              <span class="inline-flex items-center gap-1">
                <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-none" aria-hidden="true">insert_drive_file</mat-icon>
                {{ tpl.contentType }}
              </span>
              @if (structureName()) {
                <a routerLink="/fields" [queryParams]="{ structureId: tpl.fieldsStructureId }"
                   class="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline">
                  <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-none" aria-hidden="true">schema</mat-icon>
                  {{ structureName() }}
                </a>
              }
            </div>
          } @else {
            <p class="text-sm text-zinc-500 dark:text-zinc-400">View template configuration and structure fields.</p>
          }
        </div>
      </div>
      @if (template()?.id) {
        <div class="flex items-center gap-2 shrink-0">
          <a
            data-testid="template-detail-edit-btn"
            [routerLink]="['/edit-template', template()?.id]"
            class="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 cursor-pointer transition-colors"
          >
            <mat-icon class="!text-base">edit</mat-icon>
            Edit Template
          </a>
          <button
            data-testid="template-detail-menu-btn"
            type="button"
            aria-label="More template actions"
            [matMenuTriggerFor]="detailActionsMenu"
            [disabled]="isDeleting()"
            class="flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-600 hover:text-zinc-700 dark:hover:text-zinc-200 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <mat-icon class="!text-base">more_vert</mat-icon>
          </button>
          <mat-menu #detailActionsMenu="matMenu">
            <button
              mat-menu-item
              data-testid="template-detail-menu-export"
              (click)="exportTemplate()"
            >
              <mat-icon>content_copy</mat-icon>
              <span>Export</span>
            </button>
            <button
              mat-menu-item
              data-testid="template-detail-menu-toggle-blocked"
              (click)="toggleBlocked()"
            >
              <mat-icon>{{ template()?.autoBlocked ? 'lock_open' : 'block' }}</mat-icon>
              <span>{{ template()?.autoBlocked ? 'Unblock' : 'Block' }}</span>
            </button>
            <mat-divider></mat-divider>
            <button
              mat-menu-item
              data-testid="template-detail-menu-delete"
              (click)="deleteTemplate()"
            >
              <mat-icon class="!text-red-600 dark:!text-red-400">delete</mat-icon>
              <span class="text-red-600 dark:text-red-400">Delete</span>
            </button>
          </mat-menu>
        </div>
      }
    </div>
  </div>

  <!-- Status Banners: every active condition, in plain language, with the fix attached -->
  @if (template() && !isLoading() && !error()) {
    @if (template()?.autoBlocked) {
      <div data-testid="template-detail-blocked-banner"
           class="flex items-center gap-3 px-6 py-2.5 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 flex-none">
        <mat-icon class="!text-lg text-red-600 dark:text-red-400 shrink-0" aria-hidden="true">block</mat-icon>
        <p class="text-sm text-red-800 dark:text-red-300 flex-1">
          <span class="font-semibold">This template is blocked.</span>
          Documents matching it automatically go into manual review.
        </p>
        <button
          data-testid="template-detail-unblock-btn"
          type="button"
          (click)="toggleBlocked()"
          class="inline-flex items-center gap-1.5 rounded-lg border border-red-300 dark:border-red-700 bg-white dark:bg-zinc-800 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 shadow-sm hover:bg-red-50 dark:hover:bg-red-900/30 cursor-pointer transition-colors shrink-0"
        >
          <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-none">lock_open</mat-icon>
          Unblock
        </button>
      </div>
    }
    @if (isOutOfSync()) {
      <div data-testid="template-detail-out-of-sync-banner"
           class="flex items-center gap-3 px-6 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-700 flex-none">
        <mat-icon class="!text-lg text-amber-600 dark:text-amber-400 shrink-0" aria-hidden="true">sync_problem</mat-icon>
        <p class="text-sm text-amber-800 dark:text-amber-300 flex-1">
          <span class="font-semibold">Out of sync.</span>
          The {{ structureName() || 'fields' }} structure changed after this template was created — its field list no longer matches.
        </p>
        <a
          data-testid="template-detail-sync-edit-btn"
          [routerLink]="['/edit-template', template()?.id]"
          class="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-zinc-800 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 shadow-sm hover:bg-amber-50 dark:hover:bg-amber-900/30 cursor-pointer transition-colors shrink-0"
        >
          <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-none">edit</mat-icon>
          Edit template
        </a>
      </div>
    }
    @if (isAnchorMismatch()) {
      <div data-testid="template-detail-anchor-mismatch-banner"
           class="flex items-center gap-3 px-6 py-2.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-700 flex-none">
        <mat-icon class="!text-lg text-amber-600 dark:text-amber-400 shrink-0" aria-hidden="true">anchor</mat-icon>
        <p class="text-sm text-amber-800 dark:text-amber-300 flex-1">
          <span class="font-semibold">Anchor mismatch.</span>
          This template's anchors don't match its leaf's anchor fields — document matching may fail.
          @if (missingAnchorNames().length > 0) {
            Missing: <span class="font-mono text-xs">{{ missingAnchorNames().join(', ') }}</span>.
          }
          @if (extraAnchorNames().length > 0) {
            Unexpected: <span class="font-mono text-xs">{{ extraAnchorNames().join(', ') }}</span>.
          }
        </p>
        <button
          data-testid="template-detail-extract-anchors-btn"
          type="button"
          [disabled]="isReextracting()"
          (click)="reextractAnchors()"
          class="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 dark:border-amber-700 bg-white dark:bg-zinc-800 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 shadow-sm hover:bg-amber-50 dark:hover:bg-amber-900/30 cursor-pointer transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          @if (isReextracting()) {
            <div class="animate-spin rounded-full h-3.5 w-3.5 border-2 border-amber-300 border-t-amber-600"></div>
          } @else {
            <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-none">refresh</mat-icon>
          }
          Re-extract anchors
        </button>
      </div>
    }
    @if (isUnplaced()) {
      <div data-testid="template-detail-unplaced-banner"
           class="flex items-center gap-3 px-6 py-2.5 bg-zinc-50 dark:bg-zinc-800/60 border-b border-zinc-200 dark:border-zinc-700 flex-none">
        <mat-icon class="!text-lg text-zinc-500 dark:text-zinc-400 shrink-0" aria-hidden="true">location_off</mat-icon>
        <p class="text-sm text-zinc-600 dark:text-zinc-300 flex-1">
          <span class="font-semibold">Unplaced.</span>
          This template is not assigned to any anchor-tree leaf and can't be matched — assign it on the Templates page.
        </p>
        <a
          data-testid="template-detail-assign-link"
          routerLink="/templates"
          class="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-1.5 text-xs font-medium text-zinc-600 dark:text-zinc-300 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-700 cursor-pointer transition-colors shrink-0"
        >
          <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-none">open_in_new</mat-icon>
          Go to Templates
        </a>
      </div>
    }
  }

  <!-- Loading State: skeleton mirroring the final two-pane layout -->
  @if (isLoading()) {
    <div class="flex-1 min-h-0 flex gap-2 p-6 animate-pulse" aria-busy="true" aria-label="Loading template details">
      <div class="flex-[2] rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800"></div>
      <div class="flex-1 space-y-5 min-w-0">
        <div class="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-5 space-y-3">
          <div class="h-3.5 w-1/3 rounded bg-zinc-100 dark:bg-zinc-700"></div>
          <div class="h-10 w-full rounded-lg bg-zinc-100 dark:bg-zinc-700"></div>
          <div class="h-10 w-full rounded-lg bg-zinc-100 dark:bg-zinc-700"></div>
          <div class="h-10 w-full rounded-lg bg-zinc-100 dark:bg-zinc-700"></div>
        </div>
        <div class="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-5 space-y-3">
          <div class="h-3.5 w-1/4 rounded bg-zinc-100 dark:bg-zinc-700"></div>
          <div class="h-16 w-full rounded-lg bg-zinc-100 dark:bg-zinc-700"></div>
        </div>
      </div>
    </div>
  }

  <!-- Error State -->
  @if (error() && !isLoading()) {
    <div class="flex-1 flex items-center justify-center p-6">
      <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 max-w-md w-full text-center">
        <svg class="h-10 w-10 text-red-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <h3 class="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">Error Loading Template</h3>
        <p class="text-sm text-red-600 dark:text-red-400 mb-4">{{ error() }}</p>
        <button
          data-testid="template-detail-retry-btn"
          (click)="loadTemplateDetails(template()?.id || route.snapshot.paramMap.get('id')!)"
          class="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 cursor-pointer transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  }

  <!-- Content State -->
  @if (template() && !isLoading() && !error()) {
    <div class="flex-1 min-h-0">
      <app-resizable-container [initialPercent]="66" [stackBelowPx]="1024" storageKey="template-detail-split">
        <!-- Left Column: Document Preview -->
        <div left class="h-full overflow-hidden p-6 pr-4">
          <app-document-viewer
            class="block h-full"
            [pdfUrl]="template()?.fileUrl ?? null"
            [markdownUrl]="template()?.markdownFileUrl ?? null"
            title="Document Preview" />
        </div>

        <!-- Right Column: Structure Fields, Anchors, Details -->
        <div right class="h-full overflow-y-auto p-6 pl-4 space-y-5">
          <!-- Structure Fields: the primary content of this page -->
          <div class="bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 shadow-sm overflow-hidden">
            <div class="px-5 py-3 bg-zinc-50/80 dark:bg-zinc-800/80 border-b border-zinc-100 dark:border-zinc-700 flex justify-between items-center gap-3">
              <h3 class="text-sm font-semibold text-zinc-900 dark:text-zinc-100 shrink-0">Structure Fields</h3>
              @if (structureKeys().length > 0) {
                <span
                  class="inline-flex items-center rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-2.5 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 ring-1 ring-inset ring-indigo-700/10 dark:ring-indigo-400/20 shrink-0">
                  {{ structureKeys().length }} fields
                </span>
              }
            </div>
            <div class="p-5">
              @if (structureKeys().length > 10) {
                <div class="relative mb-3">
                  <mat-icon class="absolute left-2.5 top-1/2 -translate-y-1/2 !text-base !w-4 !h-4 !leading-4 text-zinc-400 dark:text-zinc-500" aria-hidden="true">search</mat-icon>
                  <input
                    data-testid="template-detail-field-filter-input"
                    type="text"
                    placeholder="Filter fields..."
                    aria-label="Filter structure fields"
                    [ngModel]="fieldFilter()"
                    (ngModelChange)="fieldFilter.set($event)"
                    class="w-full rounded-lg border border-zinc-200 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-700 py-1.5 pl-8 pr-3 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>
              }
              @if (structureKeys().length > 0) {
                <div class="space-y-2.5">
                  @for (key of filteredStructureKeys(); track key) {
                    @if (fieldsMap()[key]) {
                      <app-field-card
                        class="block"
                        [field]="fieldsMap()[key]"
                        [allFields]="allFields()"
                        [exampleTemplateValue]="template()?.structure?.[key]?.exampleValue"
                        [exampleTemplateDescription]="template()?.structure?.[key]?.description"
                        [actionable]="false"
                        [expandable]="true"
                      ></app-field-card>
                    } @else {
                      <div class="flex items-center gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2.5">
                        <mat-icon class="!text-xl text-red-500 dark:text-red-400 shrink-0" aria-hidden="true">error</mat-icon>
                        <span class="text-sm text-red-700 dark:text-red-300">Field <span class="font-mono text-xs">{{ key }}</span> not found in structure.</span>
                      </div>
                    }
                  } @empty {
                    <p class="text-sm text-zinc-500 dark:text-zinc-400 text-center py-4">No fields match your filter.</p>
                  }
                </div>
              } @else {
                <div class="text-center py-8">
                  <div class="mx-auto h-10 w-10 rounded-full bg-zinc-100 dark:bg-zinc-700 flex items-center justify-center mb-2">
                    <mat-icon class="!text-xl text-zinc-300">storage</mat-icon>
                  </div>
                  <p class="text-sm text-zinc-500 dark:text-zinc-400">No structure fields defined for this template.</p>
                </div>
              }
            </div>
          </div>

          <!-- Anchors Section -->
          @if (anchorRows().length > 0) {
            <div class="bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 shadow-sm overflow-hidden">
              <div class="px-5 py-3 bg-zinc-50/80 dark:bg-zinc-800/80 border-b border-zinc-100 dark:border-zinc-700">
                <h3 class="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Anchors</h3>
                <p class="text-[10px] text-zinc-500 dark:text-zinc-400 mt-0.5">
                  OCR values extracted from anchor fields, used to uniquely identify this template.
                </p>
              </div>
              <div class="p-5">
                <div data-testid="template-detail-anchors">
                  <div class="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50/50 dark:bg-zinc-800/50 overflow-hidden">
                    <table class="min-w-full text-sm">
                      <thead class="bg-zinc-100 dark:bg-zinc-700/60 text-xs uppercase text-zinc-500 dark:text-zinc-400">
                        <tr>
                          <th class="px-3 py-2 text-left font-medium">Field</th>
                          <th class="px-3 py-2 text-left font-medium">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (row of anchorRows(); track row.key) {
                          <tr class="border-t border-zinc-200 dark:border-zinc-700"
                              [class.bg-amber-50]="row.missing || row.extra"
                              [class.dark:bg-amber-900/20]="row.missing || row.extra"
                              [attr.data-testid]="'template-detail-anchor-row-' + row.key">
                            <td class="px-3 py-2 font-mono text-xs text-zinc-700 dark:text-zinc-300 align-top">
                              {{ row.key }}
                              @if (row.isPath) {
                                <span class="ml-1 inline-flex items-center rounded-full bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-[10px] font-sans text-zinc-500 dark:text-zinc-400"
                                      [attr.data-testid]="'template-detail-anchor-path-chip-' + row.key">path</span>
                              }
                              @if (row.extra) {
                                <span class="ml-1 inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-sans text-amber-700 dark:text-amber-300"
                                      matTooltip="This leaf does not expect this anchor — re-extract to drop it"
                                      [attr.data-testid]="'template-detail-anchor-extra-chip-' + row.key">unexpected</span>
                              }
                            </td>
                            <td class="px-3 py-2 text-zinc-900 dark:text-zinc-100 break-words">
                              @if (row.missing) {
                                <span class="inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                                  <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-none" aria-hidden="true">warning</mat-icon>
                                  Missing
                                </span>
                              } @else {
                                <span class="font-mono text-xs whitespace-pre-wrap">{{ row.value }}</span>
                              }
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          }

          <!-- Details Section: residual metadata not already in the header -->
          <div class="bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 shadow-sm overflow-hidden">
            <div class="px-5 py-3 bg-zinc-50/80 dark:bg-zinc-800/80 border-b border-zinc-100 dark:border-zinc-700">
              <h3 class="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Details</h3>
            </div>
            <dl class="p-5 space-y-3.5">
              @if (template()?.description) {
                <div>
                  <dt class="text-[10px] uppercase font-semibold text-zinc-400 dark:text-zinc-500 mb-0.5">Description</dt>
                  <dd class="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">{{ template()?.description }}</dd>
                </div>
              }
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <dt class="text-[10px] uppercase font-semibold text-zinc-400 dark:text-zinc-500 mb-0.5">Created</dt>
                  <dd class="text-sm text-zinc-700 dark:text-zinc-300">{{ formatDate(template()?.createdAt ?? '') }}</dd>
                </div>
                <div>
                  <dt class="text-[10px] uppercase font-semibold text-zinc-400 dark:text-zinc-500 mb-0.5">Updated</dt>
                  <dd class="text-sm text-zinc-700 dark:text-zinc-300">{{ formatDate(template()?.updatedAt ?? '') }}</dd>
                </div>
              </div>
            </dl>
          </div>
        </div>
      </app-resizable-container>
    </div>
  }
</div>
